import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import fetch from 'cross-fetch';
import { Field, useFormikContext } from 'formik';
import jwt_decode from 'jwt-decode';
import React, { useEffect, useRef, useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { useTranslation } from 'react-i18next';
import { Alert, Linking, TextInput } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import TextField from '@components/shared/TextField/TextField';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import { useNotification } from '@hooks/useNotification.web';
import { RegisterFormValues } from '@screens/Auth/RegisterForm';
import { useAuthContext } from '@src/context/authContext';
import theme from '@themes/theme';
import { getApiConfig } from '@utils/getApiConfig';
import { stashGenericInvitationForLogin } from '@utils/invitationWebUrl';
import { parseSignupResponse } from '@utils/rateLimit';
import { AsyncStorage, SecurityStorage } from '@utils/storage';

const SignUpWithEmail: React.FC = () => {
  const navigation = useNavigation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { t } = useTranslation('shared');
  const [loading, setLoading] = useState<boolean>(false);
  const {
    prompt,
    userInfo,
    setUserDetail,
    loading: googleAuthLoading,
  } = useGoogleAuth();
  const [appleAuthLoading, setAppleAuthLoading] = useState<boolean>(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false);
  // Readonly (what `editable={false}` maps to on web) never blocks focus —
  // blur explicitly at every point submission can start, and see the
  // pointerEvents lock below for the rest of the in-flight window.
  const emailInputRef = useRef<TextInput>(null);
  const passwordInputRef = useRef<TextInput>(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [pwdFocused, setPwdFocused] = useState(false);
  const [pwdHas8Chars, setPwdHas8Chars] = useState(false);
  const [pwdHas1Number, setPwdHas1Number] = useState(false);
  const [pwdHas1Uppercase, setPwdHas1Uppercase] = useState(false);
  const [pwdHas1SpecialChar, setPwdHas1SpecialChar] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isHoveredGoogle, setIsHoveredGoogle] = useState(false);
  const [isHoveredApple, setIsHoveredApple] = useState(false);
  const [isHoveredTerms, setIsHoveredTerms] = useState(false);
  const [isHoveredPrivacyPolicy, setIsHoveredPrivacyPolicy] = useState(false);
  const [isHoveredLogin, setIsHoveredLogin] = useState(false);

  const { setAuthToken } = useAuthContext();

  const {
    errors,
    touched,
    isValid,
    setErrors,
    setTouched,
    values,
    setFieldValue,
  } = useFormikContext<RegisterFormValues>();
  useEffect(() => {
    if (values.email_server_error) {
      setTouched({ ...touched, email: true });
    }
  }, [values.email_server_error]);

  useEffect(() => {
    if (values.password_server_error) {
      setTouched({ ...touched, password: true });
    }
  }, [values.password_server_error]);

  useEffect(() => {
    userInfo && setLoading(true);
    if (userInfo?.provider === 'Apple') {
      verifyAppleUserAccountCreatedOrNot(userInfo);
    } else {
      userInfo && verifyUserEmail(userInfo.email);
    }
  }, [userInfo]);

  const apiConfig = getApiConfig();
  const checkEmail = () => {
    setTouched({ ...touched, email: true });
    if (!errors.email) {
      emailInputRef.current?.blur();
      passwordInputRef.current?.blur();
      setIsCreatingAccount(true);
      verifyUserEmail(values.email).finally(() => setIsCreatingAccount(false));
    }
  };

  useEffect(() => {
    if (values.password) {
      checkPassword();
    } else {
      pwdHas8Chars && setPwdHas8Chars(false);
      pwdHas1Number && setPwdHas1Number(false);
      pwdHas1Uppercase && setPwdHas1Uppercase(false);
      pwdHas1SpecialChar && setPwdHas1SpecialChar(false);
    }
  }, [values.password]);

  const checkPassword = () => {
    setTouched({ ...touched, password: true });

    // Check if the password field is empty
    if (values.password) {
      const pwdHas8Chars = /.{8,}/.test(values.password);
      setPwdHas8Chars(pwdHas8Chars);

      const pwdHas1Number = /[0-9]/.test(values.password);
      setPwdHas1Number(pwdHas1Number);

      const pwdHas1Uppercase = /[A-Z]/.test(values.password);
      setPwdHas1Uppercase(pwdHas1Uppercase);

      const pwdHas1SpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(values.password);
      setPwdHas1SpecialChar(pwdHas1SpecialChar);

      // Check password against Yup validation schema
      const isPasswordValid =
        /^(?=.*[!@#$%^&*(),.?":{}|<>])(?=.*\d)(?=.*[A-Z]).{8,}$/.test(
          values.password
        );
      setIsPasswordValid(isPasswordValid);
    } else {
      setErrors({
        ...errors,
        password: 'Password is required.',
      });
    }
  };
  const disabled =
    (touched.email && !!errors.email) ||
    (!!errors.email && !isValid) ||
    (touched.password && !!errors.password) ||
    (!!errors.password && !isValid);

  const { registerForPushNotificationsAsync } = useNotification();
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await registerForPushNotificationsAsync();
        setDeviceToken(token || null);
      } catch (error) {
        console.error('Failed to get device token', error);
        setDeviceToken(null);
      }
    };

    fetchToken();
  }, []);

  const verifyAppleUserAccountCreatedOrNot = async (userInfo: any) => {
    const verifyUser = `/api/v3/verify_user.json`;
    const userPayload = {
      user: {
        email: userInfo?.email?.toLowerCase(),
        provider: userInfo?.provider,
        uid: userInfo?.id,
        is_verified: false,
        firebase_token: deviceToken,
        platform: 'web',
        // TA-10975: proves the caller holds a live Apple credential for
        // this account — see AppleIdentityTokenVerifier on the backend.
        identity_token: userInfo?.identity_token,
      },
    };
    const authEndpoint = `${apiConfig.apiUrl}${verifyUser}`;
    await fetch(authEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userPayload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        setLoading(false);
        if (response?.error) {
          if (response.auth_provider) {
            await verifyUserEmail(userInfo.email);
          } else {
            await signUpWithApplseRegister(userInfo);
          }
        } else if (response.user) {
          try {
            window.sessionStorage.setItem('setKeepSignedIn', '1');
            // An existing Apple account logging in from a generic Contacts
            // invite accepts it in AppStack (TA-11035).
            stashGenericInvitationForLogin();
            window.sessionStorage.setItem('invitation_token', '');
          } catch (e) {
            console.log('An error happened, ', e?.toString());
          } finally {
            Sentry.setUser({
              email: userPayload.user.email,
              username: userPayload.user.uid,
            });
            if (
              !response.user.phone_number ||
              response.user.phone_number === ''
            ) {
              const ns = response.user.name.split(' ');

              if (ns.length > 0) {
                setFieldValue('first_name', ns[0]);
                if (ns.length > 1) {
                  setFieldValue('last_name', ns[1]);
                } else {
                  setFieldValue('last_name', ' ');
                }
              }
              setFieldValue('email', userInfo?.email);
              setFieldValue('provider', 'Apple');
              setFieldValue('uid', userInfo?.id);

              await SecurityStorage.setItem(
                'authToken_apple',
                response.user.api_tokens[0].token
              );
              navigation.navigate('create-profile-for-google-and-apple', {
                socialSignUp: true,
              });
            } else {
              await SecurityStorage.setItem(
                'authToken',
                response.user.api_tokens[0].token
              );
              localStorage.removeItem('copy_link_toast_shown');
              setAuthToken(response.user.api_tokens[0].token);
            }
          }
        }
      })
      .catch((_err) => {});
  };

  const signUpWithApplseRegister = async (userInfoPayload: any) => {
    const email = userInfoPayload.email;
    const password = '';
    const first_name: string = userInfoPayload?.given_name;
    const last_name: string = userInfoPayload?.family_name;
    const phone_number = '';
    const provider: string = userInfoPayload?.provider;
    const uid: string = userInfoPayload?.id ?? 'id-' + email;
    const time_zone = '';
    const register = `/api/v3/users.json`;
    const registerPayload = {
      user: {
        email: email,
        password: password,
        terms_of_service: true,
        name: [first_name, last_name].filter((item) => item !== '').join(' '),
        phone_number: phone_number,
        invitation_token: '',
        provider: provider,
        uid: uid,
        time_zone: time_zone,
        firebase_token: deviceToken,
        platform: 'web',
        identity_token: userInfoPayload?.identity_token,
      },
    };
    const authEndpoint = `${apiConfig.apiUrl}${register}`;
    await fetch(authEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(registerPayload),
    })
      // TA-10842: signup shares login's throttle family. parseSignupResponse
      // short-circuits a (often bodyless) 429 to a handled message before the
      // body is parsed.
      .then((response) => parseSignupResponse(response, t))
      .then(async (response: any) => {
        if (response?.error) {
          const errType = Object.keys(response.errors ?? {})[0];
          if (
            response.error ===
            'Email has already been taken and Phone number has already been taken'
          ) {
            setErrors({
              email:
                'This email already exists. Please use other email or login.',
              phone:
                'This phone number already exists. Please use other phone number or login.',
            });
          } else {
            switch (errType) {
              case 'email':
                setErrors({
                  email:
                    response.error === 'Email has already been taken'
                      ? 'This email already exists. Please use other email or login.'
                      : response.error,
                });
                break;
              default:
                // Surface the server message (e.g. the 429 rate-limit copy)
                // when present instead of a generic fallback.
                setErrors({
                  email: response.error || 'error creating account.',
                });
            }
          }
        } else {
          Sentry.setUser({
            email: email,
            username: uid,
          });
          await SecurityStorage.setItem(
            'authToken_apple',
            response.user.api_tokens[0].token
          );
          navigation.navigate('create-profile-for-google-and-apple', {
            socialSignUp: true,
          });
        }
      })
      .catch((_err) => {
        setErrors({
          email: 'network error',
        });
      });
  };

  const verifyUserEmail = async (email: string) => {
    const sendEmailVerificationCode = async () => {
      const sendVerificationCodeURL = `${
        apiConfig.apiUrl
      }/api/v4/send_email_verification_code.json?email=${encodeURIComponent(
        email.toLowerCase()
      )}`;
      const sendVerificationResponse = await fetch(sendVerificationCodeURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const sendVerificationJson = await sendVerificationResponse.json();

      if (sendVerificationJson?.error) {
        setErrors({
          email: sendVerificationJson.error,
        });
        return false;
      }

      if (sendVerificationJson?.success === false) {
        setErrors({
          email: 'Unable to send verification code.',
        });
        return false;
      }

      return true;
    };

    const verifyUser = `/api/v3/verify_user.json`;
    const userPayload = {
      user: {
        email: email.toLowerCase(),
        is_verified: true,
        firebase_token: deviceToken,
        platform: 'web',
      },
    };
    const authEndpoint = `${apiConfig.apiUrl}${verifyUser}`;

    await fetch(authEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userPayload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.success) {
          setErrors({
            email:
              'This email already exists. Please use other email or login.',
          });
        } else if (response.error) {
          if (userInfo?.provider === 'Apple') {
            signUpWithApplseRegister(userInfo);
          } else if (userInfo) {
            navigation.navigate('create-profile-for-google-and-apple', {
              socialSignUp: true,
            });
          } else {
            const verificationSent = await sendEmailVerificationCode();

            if (verificationSent) {
              navigation.navigate('register-verify-email', {
                email: email.toLowerCase(),
              });
            }
          }
        }
      })
      .catch((_err) => {});
  };

  const appleSignInWeb = async (response: any) => {
    try {
      const localResponse = await AsyncStorage.getItem('appleUserInfo');
      let decodeEmail = '';

      const token = response.authorization.id_token;
      const decoded = jwt_decode<{ sub: string; email?: string }>(token);
      if (decoded) {
        decodeEmail = decoded.email ?? '';
      }
      if (!decoded?.sub) {
        Alert.alert('TaskTag', t('onBoarding.error.appleSignInFailed'));
        return;
      }

      const userData = localResponse ? JSON.parse(localResponse) : null;
      const userInfoPayload = {
        id: decoded.sub,
        email: response?.user?.email || userData?.user?.email || decodeEmail,
        given_name:
          response?.user?.name?.firstName || userData?.user?.name?.firstName,
        family_name:
          response?.user?.name?.lastName || userData?.user?.name?.lastName,
        provider: 'Apple',
        fromSignUp: true,
        // TA-10975: the raw token, not the decoded claims — only this is
        // what the backend can still verify server-side.
        identity_token: token,
      };
      if (response.user?.email) {
        await AsyncStorage.setItem('appleUserInfo', JSON.stringify(response));
        setUserDetail && setUserDetail(userInfoPayload);
      } else if (response) {
        setUserDetail && setUserDetail(userInfoPayload);
      } else {
        Alert.alert('TaskTag', t('onBoarding.error.emailNotFound'));
      }
    } catch (e) {
      console.log('An error happened, ', e?.toString());
    } finally {
      setAppleAuthLoading(false);
    }
  };

  const getCheckNode = (flg: boolean, label: string) => {
    return (
      <Box flexDirection='row' mb='xxs'>
        {flg ? (
          <Icon name='Check' variant='s' color='greenSecondary' />
        ) : (
          <Icon name='Dot' variant='s' />
        )}
        <Text
          ml='xxxs'
          variant='metadata'
          color={flg ? 'greenSecondary' : 'textSecondary'}>
          {label}
        </Text>
      </Box>
    );
  };

  return (
    <Box
      flex={1}
      backgroundColor='grey01'
      alignItems='center'
      justifyContent='center'>
      <ShadowBox
        variant='base'
        backgroundColor='white'
        borderRadius='m'
        style={{
          width: 492,
        }}
        onLayout={(e) => {
          setBodyWidth(e.nativeEvent.layout.width);
        }}>
        <Box
          alignItems='center'
          borderRadius='m'
          px='xl'
          mx='xxs'
          py='l'
          my='xs'
          backgroundColor='white'>
          <Icon name='Logo' width={142} height={43} marginBottom='xl' />
          <Text variant='heading2' marginVertical='xxs'>
            {t('models:onBoarding.createAnAccount')}
          </Text>
          <Text variant='webBodySecondary' marginBottom='l'>
            {t('models:onBoarding.letsGetStartedWithYour30DaysFreeTrial')}
          </Text>
          <Box width='100%' pointerEvents={isCreatingAccount ? 'none' : 'auto'}>
            <Field
              component={TextField}
              innerRef={emailInputRef}
              label={t('email')}
              name='email'
              placeholder='example@site.com'
              marginBottom='xs'
              textInputProps={{
                autoCapitalize: 'none',
                autoCorrect: false,
                keyboardType: 'email-address',
                style: {
                  color: isCreatingAccount
                    ? theme.colors.grey04
                    : theme.colors.black,
                },
                onSubmitEditing: () => {
                  setTouched({ ...touched, password: true });
                  checkPassword();
                  if (values.email && isPasswordValid && !disabled)
                    checkEmail();
                },
              }}
              customBackgroundColor={
                isCreatingAccount ? theme.colors.grey01 : undefined
              }
              editable={!isCreatingAccount}
              onFocus={() => {
                // pointerEvents='none' blocks mouse-click focus while
                // locked; this bounces back keyboard-Tab focus, which
                // pointerEvents doesn't affect.
                if (isCreatingAccount) emailInputRef.current?.blur();
              }}
              onChangeText={() => {
                if (values.email_server_error) {
                  setFieldValue('email_server_error', '');
                }
              }}
              accessibilityLabel={t('shared:pleaseEnterYourEmail')}
              validateOnChange={false}
            />
          </Box>
          <Box
            flex={1}
            alignItems='center'
            alignSelf='flex-start'
            marginBottom='l'>
            <Text variant='metadata'>
              {t(
                'models:onBoarding.aVerificationEmailWillBeSentToYouToVerifyYourAddress'
              )}
            </Text>
          </Box>
          <Box width='100%' pointerEvents={isCreatingAccount ? 'none' : 'auto'}>
            <Field
              component={TextField}
              innerRef={passwordInputRef}
              label={t('password')}
              name='password'
              accessibilityLabel={t('models:onBoarding.enterPassword')}
              placeholder={t('models:onBoarding.enterPassword')}
              marginBottom='xs'
              suffix={
                <Icon
                  name={showPassword ? 'Eye' : 'EyeOff'}
                  variant='l'
                  color='textPrimary'
                  onPress={() => setShowPassword(!showPassword)}
                />
              }
              textInputProps={{
                autoCapitalize: 'none',
                autoCorrect: false,
                secureTextEntry: !showPassword,
                style: {
                  color: isCreatingAccount
                    ? theme.colors.grey04
                    : theme.colors.black,
                },
                onSubmitEditing: async () => {
                  checkPassword();
                  if (isPasswordValid) {
                    setTouched({ ...touched, email: true });
                    if (
                      !(
                        (!!errors.email && !isValid) ||
                        (!!errors.password && !isValid)
                      )
                    ) {
                      emailInputRef.current?.blur();
                      passwordInputRef.current?.blur();
                      setIsCreatingAccount(true);
                      try {
                        await verifyUserEmail(values.email);
                      } finally {
                        setIsCreatingAccount(false);
                      }
                    }
                  }
                },
              }}
              customBackgroundColor={
                isCreatingAccount ? theme.colors.grey01 : undefined
              }
              editable={!isCreatingAccount}
              onChangeText={() => {
                checkPassword();
                if (values.password_server_error) {
                  setFieldValue('password_server_error', '');
                }
              }}
              onFocus={(b: boolean) => {
                setPwdFocused(b);
                // pointerEvents='none' blocks mouse-click focus while
                // locked; this bounces back keyboard-Tab focus, which
                // pointerEvents doesn't affect.
                if (isCreatingAccount) passwordInputRef.current?.blur();
              }}
              validateOnChange={true}
            />
          </Box>
          {pwdFocused && (
            <Box
              alignSelf='flex-start'
              marginBottom='xs'
              width={bodyWidth - 80}>
              <Text variant='metadata' color='textSecondary' mb='xxs'>
                {t('models:onBoarding.yourPasswordMustContain')}
              </Text>
              {getCheckNode(
                pwdHas8Chars,
                t('models:onBoarding.atLeast8Characters')
              )}
              {getCheckNode(
                pwdHas1Number,
                t('models:onBoarding.atLeast1Number')
              )}
              {getCheckNode(
                pwdHas1Uppercase,
                t('models:onBoarding.atLeast1UppcaseLetter')
              )}
              {getCheckNode(
                pwdHas1SpecialChar,
                t('models:onBoarding.atLeast1SpecialCharacter')
              )}
            </Box>
          )}
          <Box
            flex={1}
            flexDirection='row'
            marginTop='m'
            marginBottom='l'
            alignSelf='flex-start'>
            <Text variant='typoMetadata' color='textPrimary'>
              {t('models:onBoarding.bySigningUpIAgreeToTheTaskTag')}
              <Text
                accessibilityLabel={t('models:onBoarding.termsAndConditions')}
                variant='typoMetadata'
                color='greenSecondary'
                textDecorationLine={isHoveredTerms ? 'underline' : undefined}
                onMouseEnter={() => setIsHoveredTerms(true)}
                onMouseLeave={() => setIsHoveredTerms(false)}
                onPress={async () => {
                  await Linking.openURL(
                    'https://www.tasktag.com/terms-and-conditions'
                  );
                }}>
                {t('models:onBoarding.termsAndConditions')}
              </Text>
              {t('models:onBoarding.and')}
              <Text
                accessibilityLabel={t('models:onBoarding.privacyPolicy')}
                variant='typoMetadata'
                color='greenSecondary'
                textDecorationLine={
                  isHoveredPrivacyPolicy ? 'underline' : undefined
                }
                onMouseEnter={() => setIsHoveredPrivacyPolicy(true)}
                onMouseLeave={() => setIsHoveredPrivacyPolicy(false)}
                onPress={async () => {
                  await Linking.openURL('https://www.tasktag.com/privacy');
                }}>
                {t('models:onBoarding.privacyPolicy')}
              </Text>
              {t('models:onBoarding.dot')}
            </Text>
          </Box>
          <Button
            accessibilityLabel={t('createAccount')}
            variant='webprimary'
            color='grey04'
            fullWidth
            isSmall
            isLoading={isCreatingAccount}
            onPress={async () => {
              setTouched({ ...touched, email: true, password: true });
              if (
                !(
                  (!!errors.email && !isValid) ||
                  (!!errors.password && !isValid)
                )
              ) {
                emailInputRef.current?.blur();
                passwordInputRef.current?.blur();
                setIsCreatingAccount(true);
                try {
                  await verifyUserEmail(values.email);
                } finally {
                  setIsCreatingAccount(false);
                }
              }
            }}
            disabled={disabled || !isPasswordValid}>
            {t('createAccount')}
          </Button>
          <Box
            flexDirection='row'
            alignItems='center'
            marginTop='l'
            width={bodyWidth - 80}>
            <Box
              width={(bodyWidth - 80 - textWidth - 48) / 2}
              height={1}
              backgroundColor='grey04'
            />
            <Text
              variant='webBodySecondary'
              color='grey04'
              mx='l'
              onLayout={(e) => {
                setTextWidth(e.nativeEvent.layout.width);
              }}>
              {t('models:onBoarding.orSignUpWith')}
            </Text>
            <Box
              width={(bodyWidth - 80 - textWidth - 48) / 2}
              height={1}
              backgroundColor='grey04'
            />
          </Box>
          <Box flexDirection='row' mt='l'>
            <Box mr='xs' width={202}>
              <Button
                onMouseEnter={() => setIsHoveredGoogle(true)}
                onMouseLeave={() => setIsHoveredGoogle(false)}
                onPress={() => {
                  prompt();
                }}
                prefix={
                  <Icon
                    variant='l'
                    marginRight='xs'
                    name='Google'
                    color='textPrimary'
                    accessibilityLabel='googleIcon'
                  />
                }
                disabled={loading || isCreatingAccount}
                isLoading={googleAuthLoading}
                borderWidth={1}
                borderColor={isHoveredGoogle ? 'black' : 'grey03'}
                backgroundColor={isHoveredGoogle ? 'grey01' : 'white'}
                accessibilityLabel={t('models:onBoarding.google')}
                fullWidth
                isSmall>
                <Text variant='webBodySecondary'>
                  {t('models:onBoarding.google')}
                </Text>
              </Button>
            </Box>
            <Box width={202}>
              <AppleSignin
                /** Auth options passed to AppleID.auth.init()  */
                authOptions={{
                  clientId: process.env.APPLE_CLIENT_ID_WEB,
                  scope: 'name email',
                  redirectURI: process.env.APPLE_REDIRECT_UI,
                  state: '',
                  nonce: 'nonce',
                  usePopup: true,
                }}
                // react-apple-signin-auth types `render` as a bare `Function`
                // upstream (no generic prop signature), so `props` has no
                // narrower type to give it; `any` is required here to spread
                // it onto Button and read props.onClick below.
                render={(props: any) => (
                  <Button
                    onMouseEnter={() => setIsHoveredApple(true)}
                    onMouseLeave={() => setIsHoveredApple(false)}
                    fullWidth
                    isSmall
                    borderWidth={1}
                    borderColor={isHoveredApple ? 'black' : 'grey03'}
                    backgroundColor={isHoveredApple ? 'grey01' : 'white'}
                    accessibilityLabel={t('models:onBoarding.apple')}
                    prefix={
                      <Icon
                        variant='l'
                        name='Apple24'
                        color='textPrimary'
                        accessibilityLabel={t('models:onBoarding.apple')}
                      />
                    }
                    {...props}
                    disabled={loading || isCreatingAccount}
                    isLoading={appleAuthLoading}
                    onClick={(e: unknown) => {
                      if (appleAuthLoading) return;
                      setAppleAuthLoading(true);
                      props.onClick?.(e);
                    }}>
                    <Text variant='webBodySecondary'>
                      {t('models:onBoarding.apple')}
                    </Text>
                  </Button>
                )}
                uiType='light'
                className='apple-auth-btn'
                onSuccess={(response: any) => appleSignInWeb(response)} // default = undefined
                onError={(error: any) => {
                  // eslint-disable-next-line no-console
                  console.error('error', error);
                  setAppleAuthLoading(false);
                }}
              />
            </Box>
          </Box>
          <Box flex={1} flexDirection='row' marginTop='l'>
            <Text variant='webBodySecondary' color='black'>
              {t('models:onBoarding.alreadyHaveAnAccount')}
            </Text>
            <Text
              variant='webBodySecondary'
              color='greenSecondary'
              accessibilityLabel={t('models:onBoarding.logIn')}
              textDecorationLine={isHoveredLogin ? 'underline' : undefined}
              onMouseEnter={() => setIsHoveredLogin(true)}
              onMouseLeave={() => setIsHoveredLogin(false)}
              onPress={() => {
                navigation.navigate('login');
              }}>
              {t('models:onBoarding.logIn')}
            </Text>
          </Box>
          <Box flex={1} flexDirection='row' marginTop='l'>
            <Text variant='webBodySecondary' color='black'>
              {t('models:onBoarding.modal.needMoreHelp')}{' '}
            </Text>
            <Text
              variant='webBodySecondary'
              color='greenSecondary'
              accessibilityLabel={t('models:onBoarding.modal.bookDemo')}
              onPress={() =>
                Linking.openURL('https://portal.tasktag.com/demo')
              }>
              {t('models:onBoarding.modal.bookDemo')}
            </Text>
          </Box>
        </Box>
      </ShadowBox>
    </Box>
  );
};

export default SignUpWithEmail;
